import { ReactNode, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  ChevronLeft, ChevronRight, Search, RefreshCw, Loader2, AlertTriangle, Inbox, X,
} from 'lucide-react'
import { clsx, fa, num, compact, parseDate } from '../lib/format'

/* ---------------------------------------------------------------- کارت آمار */
export function Stat({
  label, value, hint, icon, color = '#7C5CFF', trend,
}: {
  label: string; value: ReactNode; hint?: ReactNode; icon?: ReactNode
  color?: string; trend?: number
}) {
  return (
    <div className="card relative overflow-hidden p-4">
      <div className="pointer-events-none absolute -left-8 -top-10 h-24 w-24 rounded-full opacity-25 blur-2xl"
        style={{ background: color }} />
      <div className="relative flex items-start gap-3">
        {icon && (
          <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
            style={{ background: `${color}1A`, border: `1px solid ${color}44`, color }}>
            {icon}
          </span>
        )}
        <div className="min-w-0">
          <div className="truncate text-[11px] text-slate-500">{label}</div>
          <div className="num mt-1 text-xl font-extrabold text-white">{value}</div>
          {hint && <div className="num mt-1 text-[11px] text-slate-500">{hint}</div>}
        </div>
        {trend !== undefined && (
          <span className={clsx('num mr-auto text-xs font-semibold',
            trend > 0 ? 'text-up' : trend < 0 ? 'text-down' : 'text-slate-500')}>
            {trend > 0 ? '+' : ''}{num(trend, 1)}٪
          </span>
        )}
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- بخش */
export function Panel({
  title, action, children, className, dense,
}: { title?: ReactNode; action?: ReactNode; children: ReactNode; className?: string; dense?: boolean }) {
  return (
    <div className={clsx('card', className)}>
      {(title || action) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/[.06] p-4">
          {typeof title === 'string' ? <h3 className="font-bold text-white">{title}</h3> : title}
          {action}
        </div>
      )}
      <div className={dense ? '' : 'p-4'}>{children}</div>
    </div>
  )
}

/* ---------------------------------------------------------------- نوار ابزار */
export function Toolbar({ children }: { children: ReactNode }) {
  return <div className="mb-4 flex flex-wrap items-center gap-2 [&>*]:min-w-0">{children}</div>
}

export function SearchBox({ value, onChange, placeholder = 'جستجو…' }:
  { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div className="relative w-full min-w-0 flex-1 sm:w-auto sm:min-w-[200px]">
      <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
      <input className="input !py-2 pr-9 text-xs" placeholder={placeholder}
        value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  )
}

export function Select({ value, onChange, options, className }: {
  value: string; onChange: (v: string) => void
  options: [string, string][]; className?: string
}) {
  return (
    <select className={clsx('input !w-auto !py-2 text-xs', className)}
      value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map(([v, l]) => <option key={v} value={v} className="bg-ink-900">{l}</option>)}
    </select>
  )
}

export function RefreshButton({ onClick, loading }: { onClick: () => void; loading?: boolean }) {
  return (
    <button onClick={onClick} className="btn-ghost !px-2.5 !py-2 text-xs">
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
      بروزرسانی
    </button>
  )
}

/* ---------------------------------------------------------------- جدول */
export type Column<T> = {
  key: string
  header: ReactNode
  cell: (row: T) => ReactNode
  className?: string
}

export function DataTable<T>({
  columns, rows, loading, empty = 'موردی برای نمایش وجود ندارد', minWidth = 720, onRowClick,
}: {
  columns: Column<T>[]
  rows: T[] | null
  loading?: boolean
  empty?: string
  minWidth?: number
  onRowClick?: (row: T) => void
}) {
  if (rows === null || loading) {
    return (
      <div className="space-y-2 p-3">
        {Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-11 w-full" />)}
      </div>
    )
  }
  if (rows.length === 0) {
    return (
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <Inbox className="h-7 w-7 text-slate-600" />
        <p className="text-sm text-slate-500">{empty}</p>
      </div>
    )
  }
  return (
    <>
      {/*
        ── موبایل: هر سطر یک کارت.

        جدول‌های پنل مدیریت تا ۱۰۲۰ پیکسل عرض دارند؛ روی گوشی یعنی یا کشیدن
        افقی بی‌پایان، یا کوچک‌نماییِ مرورگر تا حدی که چیزی خوانده نشود. چون
        همه صفحه‌ها از همین کامپوننت استفاده می‌کنند، این یک تغییر، هر سی‌ودو
        جدول پنل را با هم موبایل‌پسند می‌کند.

        ستون‌هایی که سرصفحه ندارند (ستون دکمه‌ها) بدون برچسب و در پایین کارت
        می‌آیند — همان‌جایی که انگشت انتظارش را دارد.
      */}
      <div className="space-y-2 p-2 md:hidden">
        {rows.map((r, i) => {
          const labeled = columns.filter((c) => c.header !== '' && c.header !== null && c.header !== undefined)
          const bare = columns.filter((c) => !labeled.includes(c))
          return (
            <div key={i}
              onClick={() => onRowClick?.(r)}
              className={clsx('rounded-xl border border-white/[.06] bg-white/[.02] p-3',
                onRowClick && 'active:bg-white/[.06]')}>
              {labeled.map((c, j) => (
                <div key={c.key}
                  className={clsx('flex items-start justify-between gap-3 py-1',
                    j === 0 && 'mb-1 border-b border-white/[.05] pb-2')}>
                  <span className="shrink-0 text-[11px] text-slate-500">{c.header}</span>
                  <span className={clsx('min-w-0 break-anywhere text-left text-xs',
                    j === 0 ? 'font-semibold text-white' : 'text-slate-300')}>
                    {c.cell(r)}
                  </span>
                </div>
              ))}
              {bare.length > 0 && (
                <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-white/[.05] pt-2">
                  {bare.map((c) => <span key={c.key}>{c.cell(r)}</span>)}
                </div>
              )}
            </div>
          )
        })}
      </div>

      <div className="hidden overflow-x-auto md:block">
      <table className="w-full text-xs" style={{ minWidth }}>
        <thead>
          <tr className="text-[11px] text-slate-500">
            {columns.map((c) => (
              <th key={c.key} className={clsx('px-3 py-2.5 text-right font-medium', c.className)}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i}
              onClick={() => onRowClick?.(r)}
              className={clsx('border-t border-white/[.05] transition',
                onRowClick && 'cursor-pointer hover:bg-white/[.04]')}>
              {columns.map((c) => (
                <td key={c.key} className={clsx('px-3 py-2.5 align-middle', c.className)}>
                  {c.cell(r)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
    </>
  )
}

export function Pager({ meta, onPage }: { meta: any; onPage: (p: number) => void }) {
  if (!meta || meta.pages <= 1) return null
  const { page, pages, total } = meta
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-white/[.06] p-3 text-xs">
      <span className="num text-slate-500">
        صفحه {fa(page)} از {fa(pages)} — مجموع {fa(total)} مورد
      </span>
      <div className="flex items-center gap-1">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)}
          className="btn-ghost !px-2 !py-1.5 disabled:opacity-30">
          <ChevronRight className="h-4 w-4" />
        </button>
        {Array.from({ length: Math.min(pages, 5) }).map((_, i) => {
          const p = Math.max(1, Math.min(pages - 4, page - 2)) + i
          if (p > pages) return null
          return (
            <button key={p} onClick={() => onPage(p)}
              className={clsx('num rounded-lg px-2.5 py-1.5',
                p === page ? 'bg-brand-500/25 text-brand-200' : 'text-slate-400 hover:bg-white/5')}>
              {fa(p)}
            </button>
          )
        })}
        <button disabled={page >= pages} onClick={() => onPage(page + 1)}
          className="btn-ghost !px-2 !py-1.5 disabled:opacity-30">
          <ChevronLeft className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

/* ---------------------------------------------------------------- نمودارها */
export function LineChart({
  data, height = 180, color = '#7C5CFF', valueFormat = (v: number) => num(v),
}: {
  data: ({ date?: string; value?: number } | number)[]
  height?: number
  color?: string
  valueFormat?: (v: number) => string
}) {
  const [hover, setHover] = useState<number | null>(null)
  if (!Array.isArray(data) || !data.length) {
    return <div className="skeleton w-full" style={{ height }} />
  }

  /**
   * نمودار هرگز نباید کل صفحه را از کار بیندازد.
   *
   * پیش از این، اگر یک فراخوان آرایه‌ی عدد خام می‌داد (به‌جای {date, value})،
   * مقدار `date` تعریف‌نشده می‌شد و
   * `Intl.DateTimeFormat().format(new Date(undefined))` خطای RangeError
   * می‌انداخت. چون این خطا در زمان رندر رخ می‌دهد، مرز خطا **تمام صفحه** را با
   * پیام «این بخش بالا نیامد» جایگزین می‌کرد — یعنی یک برچسب تاریخِ کوچک،
   * کل صفحه سامانه پیامک را می‌بست.
   *
   * حالا هر دو شکل ورودی پذیرفته می‌شود، مقدار غیرعددی صفر حساب می‌شود و
   * تاریخ نامعتبر فقط باعث نمایش‌نشدن برچسب تاریخ می‌شود.
   */
  const rows = data.map((d) => {
    const value = typeof d === 'number' ? d : Number(d?.value ?? 0)
    const date = typeof d === 'number' ? '' : String(d?.date ?? '')
    return { date, value: Number.isFinite(value) ? value : 0 }
  })

  const w = 600
  const h = height
  const max = Math.max(...rows.map((d) => d.value), 1)
  const pts = rows.map((d, i) => [(i / Math.max(rows.length - 1, 1)) * w, h - 24 - (d.value / max) * (h - 44)])
  const path = pts.map((p, i) => `${i ? 'L' : 'M'}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(' ')
  const id = 'lg' + color.replace('#', '')
  const active = Math.min(Math.max(hover ?? rows.length - 1, 0), rows.length - 1)
  const stamp = parseDate(rows[active].date)

  return (
    <div className="relative">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ height }}
        onMouseLeave={() => setHover(null)}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity="0.35" />
            <stop offset="100%" stopColor={color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0, 1, 2, 3].map((i) => (
          <line key={i} x1="0" x2={w} y1={16 + i * ((h - 40) / 3)} y2={16 + i * ((h - 40) / 3)}
            stroke="rgba(255,255,255,.05)" strokeWidth="1" />
        ))}
        <path d={`${path} L${w},${h - 24} L0,${h - 24} Z`} fill={`url(#${id})`} />
        <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" />
        {pts.map((p, i) => (
          <rect key={i} x={p[0] - w / rows.length / 2} y="0" width={w / rows.length} height={h}
            fill="transparent" onMouseEnter={() => setHover(i)} />
        ))}
        <circle cx={pts[active][0]} cy={pts[active][1]} r="4" fill={color} stroke="#05070F" strokeWidth="2" />
      </svg>
      <div className="pointer-events-none absolute inset-x-0 top-0 flex justify-between px-1 text-[10px] text-slate-500">
        <span className="num">{valueFormat(rows[active].value)}</span>
        <span className="num">
          {stamp
            ? new Intl.DateTimeFormat('fa-IR', { month: 'short', day: 'numeric' }).format(stamp)
            : ''}
        </span>
      </div>
    </div>
  )
}

export function BarList({ items, format = (v: number) => num(v) }: {
  items: { label: string; value: number; color?: string }[]
  format?: (v: number) => string
}) {
  const max = Math.max(...items.map((i) => i.value), 1)
  return (
    <div className="space-y-2.5">
      {items.map((it, i) => (
        <div key={i}>
          <div className="mb-1 flex justify-between text-xs">
            <span className="truncate text-slate-300">{it.label}</span>
            <span className="num text-slate-400">{format(it.value)}</span>
          </div>
          <div className="h-1.5 overflow-hidden rounded-full bg-white/[.06]">
            <div className="h-full rounded-full transition-all duration-500"
              style={{ width: `${(it.value / max) * 100}%`, background: it.color || '#7C5CFF' }} />
          </div>
        </div>
      ))}
    </div>
  )
}

/* ---------------------------------------------------------------- کشو */
export function Drawer({ open, onClose, title, children, wide }: {
  open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; wide?: boolean
}) {
  /*
   * روی گوشی کشو تمام عرض صفحه را می‌گیرد، پس نوارِ «جای خالی برای بستن»
   * عرضش صفر می‌شود و دیگر نمی‌شود بیرونش را لمس کرد. دو راه جایگزین:
   * ضربدرِ بالا که پایین‌تر چسبان شده، و کلید Esc برای صفحه‌کلید.
   */
  const closeRef = useRef(onClose)
  closeRef.current = onClose
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : ''
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') closeRef.current() }
    if (open) document.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = ''
      document.removeEventListener('keydown', onKey)
    }
  }, [open])
  if (!open) return null

  /*
   * ── چرا این کشو با «پورتال» بیرون از درخت صفحه رندر می‌شود ───────────
   * کشو `position: fixed` است و انتظار داریم روی تمام صفحه باز شود. اما
   * `<Drawer>` از داخل `<Panel>` صدا زده می‌شود و `Panel` کلاس `card` را
   * دارد که در index.css شامل `backdrop-blur-2xl` است. طبق استاندارد CSS
   * هر عنصری که `backdrop-filter` غیرِ `none` داشته باشد برای فرزندانش
   * «بلوکِ دربرگیرنده» می‌سازد — حتی برای فرزندانِ `fixed`.
   *
   * نتیجه دقیقاً همان چیزی بود که در پنل دیده می‌شد: کشو به‌جای کل صفحه
   * داخل همان کارتِ جدول حبس می‌شد؛ پوشش تیره فقط روی جدول می‌افتاد،
   * `h-full` برابر ارتفاع کارت می‌شد و دکمه‌های «تأیید/رد» که
   * `sticky bottom-0` هستند وسط پنل و روی تصویر مدارک می‌نشستند.
   *
   * اندازه‌گیری‌شده در کرومیوم: یک `fixed inset-0` داخل عنصری با
   * `backdrop-filter: blur(40px)` کادر ۶۰۰×۲۰۰ همان عنصر را می‌گیرد، و
   * بیرونِ آن، کادر کامل ۱۲۸۰×۸۰۰ پنجره را.
   *
   * `createPortal` کشو را مستقیم زیر `document.body` می‌برد، جایی که هیچ
   * نیایی فیلتر یا ترنسفورم ندارد. اجزای سمت کاربر (UI.tsx، Layout.tsx و
   * Popover.tsx) از ابتدا همین کار را می‌کردند؛ فقط پنل مدیریت جا مانده
   * بود. هر پوشش تمام‌صفحه‌ی تازه‌ای هم باید از همین راه برود.
   */
  return createPortal(
    /*
     * `h-dvh` کنار `inset-0`: روی گوشی، ارتفاعِ عنصرِ `fixed` برابرِ
     * «نمای بزرگ» است، یعنی وقتی نوار آدرس مرورگر دیده می‌شود کفِ کشو —
     * جایی که دکمه‌های تأیید و رد هستند — زیر همان نوار پنهان می‌ماند.
     * `dvh` ارتفاع را با نمای واقعی هماهنگ می‌کند. چون top و height با هم
     * تعریف شده‌اند، مرورگر `bottom` را نادیده می‌گیرد و مرورگرهای قدیمی
     * که `dvh` را نمی‌شناسند به همان رفتار قبلی برمی‌گردند.
     */
    <div className="fixed inset-0 z-[120] flex h-dvh">
      <div className="flex-1 bg-ink-950/70 backdrop-blur-sm" onClick={onClose} />
      <aside className={clsx('h-full overflow-y-auto border-l border-white/10 bg-ink-900/97 p-5 shadow-card backdrop-blur-2xl',
        wide ? 'w-full max-w-3xl' : 'w-full max-w-md')}>
        {/*
          سربرگ چسبان است تا ضربدرِ بستن با اسکرول از دید بیرون نرود —
          روی گوشی که فهرست مدارک بلند است، ضربدر تنها راهِ بستن است.
          `-top-5` دقیقاً پدینگِ بالای کشو را خنثی می‌کند؛ با `top-0` یک
          نوارِ ۲۰ پیکسلی بالای سربرگ می‌ماند که محتوا از داخلش رد می‌شود.
        */}
        <div className="sticky -top-5 z-10 -mx-5 -mt-5 mb-5 flex items-center justify-between gap-3 border-b border-white/10 bg-ink-900/98 px-5 py-4 backdrop-blur-xl">
          <div className="font-bold text-white">{title}</div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-white/10 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        {children}
      </aside>
    </div>,
    document.body,
  )
}

/* ---------------------------------------------------------------- تأیید */
export function ConfirmButton({
  label, onConfirm, variant = 'down', question = 'مطمئن هستید؟', className, icon,
}: {
  label: ReactNode; onConfirm: () => void
  variant?: 'down' | 'up' | 'ghost'; question?: string; className?: string; icon?: ReactNode
}) {
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    if (!armed) return
    const id = setTimeout(() => setArmed(false), 4000)
    return () => clearTimeout(id)
  }, [armed])
  const cls = { down: 'btn-down', up: 'btn-up', ghost: 'btn-ghost' }[variant]
  return (
    <button
      onClick={() => (armed ? (setArmed(false), onConfirm()) : setArmed(true))}
      className={clsx(armed ? 'btn-down' : cls, '!px-3 !py-1.5 text-xs', className)}
    >
      {armed ? <AlertTriangle className="h-3.5 w-3.5" /> : icon}
      {armed ? question : label}
    </button>
  )
}

/* ---------------------------------------------------------------- برچسب وضعیت */
const STATUS: Record<string, [string, string]> = {
  open: ['info', 'باز'], partial: ['gold', 'پرشده جزئی'], filled: ['up', 'تکمیل'],
  canceled: ['muted', 'لغو'], pending: ['gold', 'در انتظار'], confirmed: ['up', 'موفق'],
  rejected: ['down', 'رد شده'], approved: ['up', 'تأیید'], failed: ['down', 'ناموفق'],
  queued: ['muted', 'در صف'], review: ['gold', 'در انتظار بررسی'], sending: ['info', 'در حال ارسال'],
  sent: ['info', 'ارسال شد'], credited: ['up', 'واریز شد'], ignored: ['muted', 'نادیده'],
  active: ['up', 'فعال'], finished: ['muted', 'پایان‌یافته'], closed: ['muted', 'بسته'],
  answered: ['info', 'پاسخ داده شد'],
}
const KIND: Record<string, string> = {
  up: 'bg-up/12 text-up border-up/25',
  down: 'bg-down/12 text-down border-down/25',
  info: 'bg-brand-500/12 text-brand-200 border-brand-500/25',
  gold: 'bg-gold/12 text-gold border-gold/25',
  muted: 'bg-white/[.05] text-slate-400 border-white/10',
}

export function Status({ value }: { value: string }) {
  const [kind, label] = STATUS[value] ?? ['muted', value]
  return <span className={clsx('inline-flex rounded-full border px-2 py-0.5 text-[11px]', KIND[kind])}>{label}</span>
}

export function Tag({ children, kind = 'muted' }: { children: ReactNode; kind?: keyof typeof KIND }) {
  return <span className={clsx('inline-flex rounded-full border px-2 py-0.5 text-[11px]', KIND[kind])}>{children}</span>
}

export const money = (v?: number | null) => (v === null || v === undefined ? '—' : compact(v))
